/**
 * Custom hook for video platform API operations
 * Centralizes all API calls with proper authentication
 */

import { useController } from "@rest-hooks/react";
import {
  VideoPlatformResource,
  MeetingCategoryOption,
} from "../../../api/video_platform";
import { MEETING_QUERY_PARAMS } from "../utils";
import { CreateMeetingFormData } from "../types";

export const useVideoPlatformApi = () => {
  const { fetch } = useController();

  const refreshMeetings = () => {
    return fetch(VideoPlatformResource.getMeetings, MEETING_QUERY_PARAMS);
  };

  const createMeeting = async (formData: CreateMeetingFormData) => {
    return fetch(VideoPlatformResource.createMeeting, formData);
  };

  const deleteMeeting = async (meetingId: string) => {
    return fetch(VideoPlatformResource.deleteMeeting.extend({ path: `/admin/video-platform/meetings/${meetingId}` }));
  };

  const refreshAttendees = (meetingId: string) => {
    return fetch(VideoPlatformResource.getMeetingAttendees, { meetingId });
  };

  const updateAttendeeCapabilities = async (
    meetingId: string,
    attendeeId: string,
    capabilities: {
      audio?: { value?: string; lock?: boolean };
      video?: { value?: string; lock?: boolean };
      content?: { value?: string; lock?: boolean };
    }
  ) => {
    return fetch(VideoPlatformResource.updateAttendeeCapabilities, {
      meetingId,
      attendeeId,
    }, capabilities);
  };

  const batchUpdateAttendeeCapabilities = async (
    meetingId: string,
    operation: "mute_all" | "unmute_all" | "disable_video_all" | "enable_video_all" | "disable_content_all" | "enable_content_all",
    excludeHosts: boolean = true,
    lock: boolean = false
  ) => {
    return fetch(VideoPlatformResource.batchUpdateAttendeeCapabilities, {
      meetingId,
    }, {
      operation,
      excludeHosts,
      lock,
    });
  };

  const getCategories = async (): Promise<{ categories: MeetingCategoryOption[] }> => {
    return fetch(VideoPlatformResource.getCategories);
  };

  const updateMeeting = async (
    meetingId: string,
    data: { category?: string; title?: string; description?: string; isPublicVisible?: boolean }
  ) => {
    return fetch(VideoPlatformResource.updateMeeting, { id: meetingId }, data);
  };

  return {
    refreshMeetings,
    createMeeting,
    deleteMeeting,
    refreshAttendees,
    updateAttendeeCapabilities,
    batchUpdateAttendeeCapabilities,
    getCategories,
    updateMeeting,
  };
};
